/**
 * VideoWaiting.jsx — placeholder shown inside a chat bubble while a
 * video-generation job is polling. Rotates through friendly waiting
 * phrases every ~6.5s and tracks elapsed time so the user knows we
 * haven't hung.
 *
 * Kept as a leaf component (no external state) so it can be dropped in
 * anywhere a video job is in-flight.
 */
import { useEffect, useState } from "react";
import * as L from "lucide-react";

const PHRASES = [
  "Reading the light in your prompt…",
  "Choosing where the camera stands…",
  "Setting the pace — this feels like a slow zoom.",
  "Composing frame by frame.",
  "Almost there — laying down the last few seconds.",
];

export default function VideoWaiting({ testid, prompt, duration = 4, size = "1280x720", startedAt }) {
  const [phraseIdx, setPhraseIdx] = useState(0);
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    const t = setInterval(
      () => setElapsed(Math.floor((Date.now() - (startedAt || Date.now())) / 1000)),
      1000,
    );
    const p = setInterval(() => setPhraseIdx(i => (i + 1) % PHRASES.length), 6500);
    return () => { clearInterval(t); clearInterval(p); };
  }, [startedAt]);
  const mm = String(Math.floor(elapsed / 60)).padStart(1, "0");
  const ss = String(elapsed % 60).padStart(2, "0");
  return (
    <div
      className="mt-1 mb-2 w-80 rounded-2xl border border-dashed border-[var(--rammy-cyan)]/40 bg-[var(--rammy-cyan)]/5 p-4"
      data-testid={testid}
    >
      <div className="flex items-center gap-2 text-[var(--rammy-cyan-dark)]">
        <L.Film size={16}/>
        <div className="text-[11px] uppercase tracking-widest font-semibold">Weaving your scene…</div>
      </div>
      <div className="mt-3 flex items-center gap-2">
        <L.Loader2 size={16} className="animate-spin text-[var(--rammy-cyan-dark)]"/>
        <div className="text-[13px] text-[var(--rammy-text)] italic">{PHRASES[phraseIdx]}</div>
      </div>
      {prompt && (
        <div className="mt-2 text-[11px] text-[var(--rammy-text-muted)] line-clamp-2">&ldquo;{prompt}&rdquo;</div>
      )}
      <div className="mt-3 flex items-center justify-between text-[10px] uppercase tracking-widest text-[var(--rammy-text-muted)]">
        <span>{duration}s · {size}</span>
        <span data-testid={`${testid}-elapsed`}>{mm}:{ss}</span>
      </div>
    </div>
  );
}
